import { SearchIcon } from "lucide-react";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
} from "react";
import { useSearchParams } from "react-router-dom";

import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import { searchMessages } from "@/components/search/search.intl";
import DataTableSearchInput from "@/components/search/SearchInput";
import { Button } from "@/components/ui/button";

interface SearchProps {
  search?: string;
  updateParams: (key: string, value: string) => void;
  searchKey?: string;
  scope?: string;
}

/**
 * Toggleable search input with keyboard shortcut (Cmd/Ctrl+K) support.
 *
 * @param {SearchProps} props - Search configuration.
 * @param {React.Ref<{ resetSearch: () => void }>} ref - Imperative handle for programmatic reset.
 * @returns {JSX.Element} Search button or expanded search input.
 */
const Search = forwardRef((props: SearchProps, ref): JSX.Element => {
  const { search, updateParams, searchKey = "search", scope } = props;
  const t = useIntlMessages(searchMessages);
  const [searchParams] = useSearchParams();

  const effectiveSearchKey = scope ? `${scope}_${searchKey}` : searchKey;
  const searchValue = searchParams.get(effectiveSearchKey) || "";
  const [showSearch, setShowSearch] = React.useState(false);

  useEffect(() => {
    if (searchValue) {
      setShowSearch(true);
    }
  }, [searchValue]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        if (!showSearch) {
          setShowSearch(true);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return (): void => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showSearch]);

  const clearSearch = useCallback((): void => {
    setShowSearch(false);
    updateParams(effectiveSearchKey, "");
  }, [effectiveSearchKey, updateParams]);

  const handleSearch = useCallback(
    (searchTerm: string): void => {
      updateParams(effectiveSearchKey, searchTerm);
    },
    [effectiveSearchKey, updateParams],
  );

  useImperativeHandle(
    ref,
    () => ({
      resetSearch: (): void => {
        clearSearch();
      },
    }),
    [clearSearch],
  );

  return search && !showSearch ? (
    <Button className='h-9' variant='ghost' onClick={() => setShowSearch(true)}>
      <SearchIcon className='w-4 h-4 mr-2' />
      {t("search")}
    </Button>
  ) : (
    <DataTableSearchInput
      autoFocus
      value={searchValue}
      onChange={(searchTerm) => handleSearch(searchTerm ?? "")}
      onExit={clearSearch}
    />
  );
});

Search.displayName = "Search";

export default Search;
